import { SelectFieldOption } from "../../beta/components/SelectField/types";
import { SectionMeta, SelectItem } from "../types/selectFieldInternalTypes";
import { NormalizedPinnedSection } from "../hooks/usePinnedOptions";
import { NormalizedGroupSection } from "../hooks/useGroupedOptions";

export function toSelectItem(option: SelectFieldOption): SelectItem {
  return {
    id: option.id,
    type: "option",
    disabled: option.disabled ?? false,
    original: option,
  };
}

type BuildSelectItemsParams = {
  prefixItems?: SelectItem[];
  pinnedSections: NormalizedPinnedSection[];
  groupSections: NormalizedGroupSection[];
  ungroupedItems: SelectItem[];
};

/**
 * Assembles the flat items array and sectionsMeta.
 * Order: prefixItems → pinned sections → group sections → ungrouped items.
 * Multi-select hooks pass bulk action items (select-all / select-filtered) as prefixItems;
 * single-select hooks omit it.
 */
export function buildSelectItems({
  prefixItems,
  pinnedSections,
  groupSections,
  ungroupedItems,
}: BuildSelectItemsParams): {
  items: SelectItem[];
  sectionsMeta: SectionMeta[];
} {
  const items: SelectItem[] = [];
  const meta: SectionMeta[] = [];

  if (prefixItems) {
    items.push(...prefixItems);
  }

  pinnedSections.forEach((section) => {
    const startIndex = items.length;

    section.options.forEach((option) => {
      items.push({
        id: option.id + "-pinned",
        type: "pinned-option",
        original: option,
        disabled: option.disabled ?? false,
      });
    });

    const endIndex = items.length;

    if (endIndex > startIndex || section.loading) {
      meta.push({
        type: "pinned",
        label: section.label,
        startIndex,
        endIndex,
        loading: section.loading,
      });
    }
  });

  groupSections.forEach((section) => {
    const startIndex = items.length;

    section.options.forEach((option) => {
      items.push({
        id: option.id,
        type: "grouped-option",
        original: option,
        disabled: option.disabled ?? false,
      });
    });

    const endIndex = items.length;

    if (endIndex > startIndex) {
      meta.push({
        type: "group",
        label: section.label,
        startIndex,
        endIndex,
      });
    }
  });

  items.push(...ungroupedItems);

  return { items, sectionsMeta: meta };
}
